/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { QueryClient, queryOptions, useQuery } from '@tanstack/react-query';

export const guestLoginKey = ['sso', 'is_guest_login'];

const guestLoginStorageKey = guestLoginKey.join('_');

/**
 * Handles guest login by persisting a boolean flag and updating React Query cache.
 *
 * - Stores guest login boolean in AsyncStorage.
 * - Invalidates the guest login query so subscribers refetch the latest value.
 *
 * @param queryClient React Query client instance.
 * @param isGuestLogin Whether the current session is guest mode.
 */
export const handleGuestLogin = async (
  queryClient: QueryClient,
  isGuestLogin: boolean,
) => {
  if (isGuestLogin) {
    await AsyncStorage.setItem(guestLoginStorageKey, 'true');
  } else {
    await AsyncStorage.removeItem(guestLoginStorageKey);
  }

  await queryClient.invalidateQueries({ queryKey: guestLoginKey });
};

/**
 * React Query options for fetching the guest login flag from AsyncStorage.
 *
 * - Reads the locally persisted isGuestLogin flag.
 * - Does not make any network calls.
 *
 * @returns React Query options object.
 */
const guestLoginQueryOptions = () =>
  queryOptions<boolean>({
    queryKey: guestLoginKey,
    retry: false,
    queryFn: async () => {
      const isGuestLogin = await AsyncStorage.getItem(guestLoginStorageKey);
      return isGuestLogin === 'true';
    },
  });

/**
 * Hook that subscribes to the isGuestLogin boolean stored in AsyncStorage.
 *
 * - Automatically keeps components updated with changes to isGuestLogin state.
 */
export const useGuestLogin = () => {
  return useQuery<boolean>(guestLoginQueryOptions());
};
